import { CheckoutDetails } from "@features/checkout/types";
import { formatPrice } from "@utils/helpers";
import Feetooltip from "./feetoolTip";
import { Card, Text } from "@components/theming";
import {
  getDeliveryFeesRange,
  getTransactionFeesRange,
} from "@features/cartDetails/utils/feesUtil";
import { useLocale, useTranslations } from "next-intl";
import { useIsShopsite } from "@context/DomainContext";
import { Skeleton } from "@radix-ui/themes";

interface Props {
  checkoutDetails: CheckoutDetails | undefined;
  isLoading: boolean;
}

const CartFee = ({ checkoutDetails, isLoading }: Props) => {
  const feeTranslations = useTranslations("Checkout.Cart.tickets.fees");
  const locale = useLocale();
  const isShopsite = useIsShopsite();

  return (
    <Card
      size={{ initial: "2", md: "3" }}
      shopsiteClasses="rounded-lg bg-gray-50 px-4 py-6 sm:p-6 lg:p-8"
    >
      <dl className="space-y-4">
        <div className="flex items-center justify-between">
          <Text as="span" sharedClasses="text-sm">
            {feeTranslations("subtotal")}
          </Text>
          <Skeleton
            loading={isLoading}
            className={isShopsite ? "rounded-md" : ""}
          >
            <Text as="span" sharedClasses="ml-4 text-sm">
              {formatPrice(
                locale,
                checkoutDetails?.Currency.CurrencyCode ?? "nzd",
                checkoutDetails?.ItemPriceSum ?? 0
              )}
            </Text>
          </Skeleton>
        </div>
        {checkoutDetails?.Fees.map((fee) => (
          <div key={fee.Name} className="flex items-center justify-between">
            <Text
              as="span"
              sharedClasses="text-sm"
              whitelabelClasses="text-[color:var(--gray-11)]"
              shopsiteClasses="text-gray-500"
            >
              {`${
                fee.Name === "Booking Fee"
                  ? feeTranslations("bookingfee.heading")
                  : fee.Name
              }${
                fee.Name === "Transaction Fee" && checkoutDetails.PaymentType.Id
                  ? ` - ${checkoutDetails.PaymentType.Name}`
                  : ""
              }`}
            </Text>
            <Skeleton
              loading={isLoading}
              className={isShopsite ? "rounded-md" : ""}
            >
              <Text
                as="span"
                sharedClasses="ml-4 text-sm font-medium"
                whitelabelClasses="text-[color:var(--gray-11)]"
                shopsiteClasses="text-gray-500"
              >
                {formatPrice(
                  locale,
                  checkoutDetails.Currency.CurrencyCode,
                  fee.Value
                )}
              </Text>
            </Skeleton>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <Text
            as="span"
            sharedClasses="text-sm flex items-center gap-x-2"
            whitelabelClasses="text-[color:var(--gray-11)]"
            shopsiteClasses="text-gray-500"
          >
            {feeTranslations("delivery.heading")}{" "}
            {checkoutDetails?.DeliveryType.Id &&
              `- ${checkoutDetails.DeliveryType.Name}`}
            {checkoutDetails && checkoutDetails.DeliveryTypes.length > 1 ? (
              <Feetooltip
                title={feeTranslations("delivery.tooltiptitle")}
                description={feeTranslations("delivery.tooltipdesc")}
                fees={checkoutDetails.DeliveryTypes.map((delivery) => ({
                  id: delivery.Id,
                  description: delivery.Description,
                  name: delivery.Name,
                  price: delivery.Price,
                }))}
                currency={checkoutDetails.Currency.CurrencyCode}
              />
            ) : null}
          </Text>
          <Skeleton
            loading={isLoading}
            className={isShopsite ? "rounded-md" : ""}
          >
            <Text
              as="span"
              sharedClasses="ml-4 text-sm font-medium"
              whitelabelClasses="text-[color:var(--gray-11)]"
              shopsiteClasses="text-gray-500"
            >
              {checkoutDetails?.DeliveryType.Id
                ? checkoutDetails.DeliveryType.Price === 0
                  ? feeTranslations("free")
                  : formatPrice(
                      locale,
                      checkoutDetails.Currency.CurrencyCode,
                      checkoutDetails.DeliveryType.Price
                    )
                : checkoutDetails
                ? getDeliveryFeesRange(locale, checkoutDetails, feeTranslations)
                : "--------"}
            </Text>
          </Skeleton>
        </div>
        {!checkoutDetails?.Fees.some((fee) => fee.Name === "Transaction Fee") &&
          !checkoutDetails?.PaymentTypes.some(
            (type) => type.Type === "creditcard" && type.PaidBy > 0
          ) && (
            <div className="flex items-center justify-between">
              <Text
                as="span"
                sharedClasses="text-sm flex items-center gap-x-2"
                whitelabelClasses="text-[color:var(--gray-11)]"
                shopsiteClasses="text-gray-500"
              >
                {feeTranslations("transaction.heading")}{" "}
                {checkoutDetails?.PaymentType.Id &&
                  `- ${checkoutDetails?.PaymentType.Name}`}
                {checkoutDetails && checkoutDetails.PaymentTypes.length > 1 && (
                  <Feetooltip
                    title={feeTranslations("transaction.tooltiptitle")}
                    description={feeTranslations("transaction.tooltiptitle")}
                    fees={checkoutDetails.PaymentTypes.map((payment) => ({
                      id: payment.Id,
                      description: payment.Description,
                      name: payment.Name,
                      price: payment.PaidBy === 0 ? payment.Price : 0,
                    }))}
                    currency={checkoutDetails.Currency.CurrencyCode}
                  />
                )}
              </Text>
              <Skeleton
                loading={isLoading}
                className={isShopsite ? "rounded-md" : ""}
              >
                <Text
                  as="span"
                  sharedClasses="ml-4 text-sm font-medium"
                  whitelabelClasses="text-[color:var(--gray-11)]"
                  shopsiteClasses="text-gray-500"
                >
                  {checkoutDetails?.PaymentType.Id &&
                  checkoutDetails.PaymentType.Price
                    ? checkoutDetails.PaymentType.PaidBy !== 0
                      ? feeTranslations("free")
                      : formatPrice(
                          locale,
                          checkoutDetails.Currency.CurrencyCode,
                          checkoutDetails.PaymentType.Price
                        )
                    : checkoutDetails
                    ? getTransactionFeesRange(
                        locale,
                        checkoutDetails,
                        feeTranslations
                      )
                    : "--------"}
                </Text>
              </Skeleton>
            </div>
          )}
        {checkoutDetails?.PaymentTypes.some(
          (type) => type.Type === "creditcard" && type.PaidBy > 0
        ) && (
          <div className="flex items-center justify-between">
            <Text
              as="span"
              className="text-sm flex items-center gap-x-2"
              whitelabelClasses="text-[color:var(--gray-11)]"
              shopsiteClasses="text-gray-500"
            >
              {feeTranslations("transaction.heading")}{" "}
              {checkoutDetails.PaymentType.Id &&
                `- ${checkoutDetails.PaymentType.Name}`}
              {checkoutDetails && checkoutDetails.PaymentTypes.length > 1 && (
                <Feetooltip
                  title={feeTranslations("transaction.tooltiptitle")}
                  description={feeTranslations("transaction.tooltiptitle")}
                  fees={checkoutDetails.PaymentTypes.map((payment) => ({
                    id: payment.Id,
                    description: payment.Description,
                    name: payment.Name,
                    price: payment.PaidBy === 0 ? payment.Price : 0,
                  }))}
                  currency={checkoutDetails.Currency.CurrencyCode}
                />
              )}
            </Text>
            <Text
              as="span"
              className="ml-4 text-sm font-medium"
              whitelabelClasses="text-[color:var(--gray-11)]"
              shopsiteClasses="text-gray-500"
            >
              {feeTranslations("free")}
            </Text>
          </div>
        )}
        <div className="flex items-center justify-between">
          <Text as="span" className="text-base">
            {feeTranslations("total")}
          </Text>
          <Skeleton
            loading={isLoading}
            className={isShopsite ? "rounded-md" : ""}
          >
            <Text as="span" className="ml-4 text-base font-medium">
              {formatPrice(
                locale,
                checkoutDetails?.Currency.CurrencyCode ?? "nzd",
                checkoutDetails?.PaymentTotal ?? 0
              )}
            </Text>
          </Skeleton>
        </div>
      </dl>
      {checkoutDetails &&
        (!checkoutDetails.PaymentType.Id || !checkoutDetails.DeliveryType.Id) &&
        checkoutDetails.PaymentTypes.length > 1 &&
        !checkoutDetails?.PaymentTypes.some(
          (type) => type.Type === "creditcard"
        ) && (
          <Text
            as="p"
            sharedClasses="mt-1 text-xs text-right"
            whitelabelClasses="text-[color:var(--accent-11)]"
            shopsiteClasses="text-indigo-600"
          >
            {feeTranslations("totalpricewarning", {
              missingid:
                !checkoutDetails.PaymentType.Id &&
                !checkoutDetails.DeliveryType.Id
                  ? "both"
                  : !checkoutDetails.PaymentType.Id
                  ? "payment"
                  : "delivery",
            })}
          </Text>
        )}
      {checkoutDetails &&
        !checkoutDetails.PaymentType.Id &&
        checkoutDetails.PaymentTypes.length > 1 &&
        checkoutDetails.PaymentTypes.some(
          (type) => type.Type === "creditcard"
        ) && (
          <Text
            as="p"
            sharedClasses="mt-1 text-xs text-right"
            whitelabelClasses="text-[color:var(--accent-11)]"
            shopsiteClasses="text-indigo-600"
          >
            {feeTranslations("desc")}
          </Text>
        )}
    </Card>
  );
};

export default CartFee;
